export const VEHICLE_TYPES = ['TRUCK', 'TRAILER', 'SOLO'] as const
export type VehicleType = typeof VEHICLE_TYPES[number]

export const FLEET_CAPABILITIES = ['CONTAINER', 'FTL', 'REEFER', 'ADR', 'OVERSIZED', 'BULK'] as const
export type FleetCapability = typeof FLEET_CAPABILITIES[number]

export const EURO_CLASSES = ['EURO3', 'EURO4', 'EURO5', 'EURO6', 'EURO6D'] as const
export type VehicleEuroClass = typeof EURO_CLASSES[number]

export const FUEL_TYPES = ['DIESEL', 'LNG', 'CNG', 'ELECTRIC', 'HYBRID'] as const
export type VehicleFuelType = typeof FUEL_TYPES[number]

export const GPS_PROVIDERS = ['GBOX', 'TELTONIKA', 'MANUAL', 'PWA'] as const
export type VehicleGpsProvider = typeof GPS_PROVIDERS[number]

export type VehicleSpecs = Record<string, unknown> | null

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

export function normalizeRegistrationPlate(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
}

export function formatRegistrationPlate(value: string): string {
  return collapseWhitespace(value).toUpperCase()
}

export function normalizeCapability(value: string): FleetCapability {
  return value.trim().toUpperCase() as FleetCapability
}

export function normalizeCapabilities(values: readonly string[]): FleetCapability[] {
  const normalized = Array.from(
    new Set(
      values
        .map((value) => normalizeCapability(value))
        .filter((value): value is FleetCapability =>
          (FLEET_CAPABILITIES as readonly string[]).includes(value),
        ),
    ),
  )
  normalized.sort()
  return normalized
}

export function buildCapabilitiesSearch(values: readonly string[]): string {
  const normalized = normalizeCapabilities(values)
  return normalized.length > 0 ? `,${normalized.join(',')},` : ','
}

export function buildVehicleSearchText(input: {
  registrationPlate: string
  registrationPlateNormalized: string
  brand?: string | null
  model?: string | null
}): string {
  return [
    input.registrationPlate,
    input.registrationPlateNormalized,
    input.brand ?? null,
    input.model ?? null,
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => collapseWhitespace(value).toLowerCase())
    .join(' ')
}

export function normalizeOptionalText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const normalized = collapseWhitespace(value)
  return normalized.length > 0 ? normalized : null
}

export function normalizeSpecs(value: unknown): VehicleSpecs {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

export function shouldVehicleRequireGpsDevice(provider: VehicleGpsProvider | null | undefined): boolean {
  return provider === 'GBOX' || provider === 'TELTONIKA'
}

export function isTrailerType(type: VehicleType): boolean {
  return type === 'TRAILER'
}

export function normalizeDateOnly(value: Date | string | null | undefined): Date | null {
  if (value == null) return null
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()))
  }
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const candidate = new Date(trimmed.length <= 10 ? `${trimmed}T00:00:00.000Z` : trimmed)
  if (Number.isNaN(candidate.getTime())) return null
  return new Date(Date.UTC(candidate.getUTCFullYear(), candidate.getUTCMonth(), candidate.getUTCDate()))
}

export function toIsoDate(value: Date | null | undefined): string | null {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null
  return value.toISOString().slice(0, 10)
}
